// The house style for words travellers read (question labels, document
// names and instructions, FAQs): sentence case, UK spelling, curly
// apostrophes, no doubled words or spaces. Each check returns
// { key, short, text, fix? }: `short` for a Check column, `text` to read,
// `fix` the whole text corrected, for a one-click "Use this". None of them
// blocks a save: a person decides.

// US spellings that show up in visa forms, and the UK word.
const UK = {
  traveling: "travelling",
  traveled: "travelled",
  traveler: "traveller",
  travelers: "travellers",
  color: "colour",
  colors: "colours",
  center: "centre",
  centers: "centres",
  organization: "organisation",
  organizations: "organisations",
  authorization: "authorisation",
  authorized: "authorised",
  recognized: "recognised",
  enrollment: "enrolment",
  favorite: "favourite",
  labor: "labour",
  fulfill: "fulfil",
  canceled: "cancelled",
  cancelation: "cancellation",
  license: "licence",
  catalog: "catalogue",
  apologize: "apologise",
  analyze: "analyse",
};

// Words that keep their capital in sentence case.
const KEEP = new Set([
  "I", "EU", "EEA", "UK", "US", "USA", "UAE", "USD", "EUR", "LBP", "ID", "PDF", "JPG", "PNG", "DS-160", "ETA", "ESTA", "IBAN",
  "Schengen", "English", "Arabic", "French", "Lebanon", "Lebanese", "Syria", "Syrian", "Europe", "European", "Dubai", "Beirut",
  "France", "Germany", "Italy", "Spain", "Greece", "Switzerland", "Iceland", "Denmark", "Japan", "Canada", "Australia", "America", "American",
  "British", "Kingdom", "States", "Emirates", "Arab", "United", "OMT", "TLS", "VFS", "BLS", "Mr", "Mrs", "Ms",
]);

const ARABIC = /[؀-ۿ]/;

const caseWord = (word, index) => {
  if (index === 0) return word;
  const bare = word.replace(/[^\p{L}\p{N}-]/gu, "");
  if (!bare || KEEP.has(bare) || bare === bare.toUpperCase() || /\d/.test(bare)) return word;
  return word.charAt(0) === word.charAt(0).toUpperCase() ? word.charAt(0).toLowerCase() + word.slice(1) : word;
};

/** "Country of Birth" -> "Country of birth"; null when it is sentence case already. */
export function sentenceCase(text) {
  const value = String(text ?? "");
  const parts = value.split(/(\s+)/);
  let index = -1;
  const fixed = parts.map((part) => (/^\s+$/.test(part) || !part ? part : caseWord(part, (index += 1)))).join("");
  return fixed !== value ? fixed : null;
}

/** "Employer's" -> "Employer’s" (apostrophes only, not quotation marks). */
export const curlyApostrophes = (text) => String(text ?? "").replace(/(\p{L})'(\p{L})/gu, "$1’$2").replace(/(s)'(\s|$)/g, "$1’$2");

function ukSpelling(text) {
  let found = null;
  const fixed = String(text ?? "").replace(/\b[A-Za-z]+\b/g, (word) => {
    const uk = UK[word.toLowerCase()];
    if (!uk) return word;
    found = found || { us: word, uk };
    return word.charAt(0) === word.charAt(0).toUpperCase() ? uk.charAt(0).toUpperCase() + uk.slice(1) : uk;
  });
  return found ? { ...found, fixed } : null;
}

const firstDifference = (a, b) => {
  let i = 0;
  while (i < a.length && a[i] === b[i]) i += 1;
  return i;
};

// A long answer is quoted by the few words around the change, not whole.
function around(text, at, reach = 30) {
  if (text.length <= reach * 2 + 10) return text;
  let start = Math.max(0, at - reach);
  let end = Math.min(text.length, at + reach);
  if (start > 0) start = text.indexOf(" ", start) + 1 || start;
  if (end < text.length) end = text.lastIndexOf(" ", end) > at ? text.lastIndexOf(" ", end) : end;
  return `${start > 0 ? "…" : ""}${text.slice(start, end).trim()}${end < text.length ? "…" : ""}`;
}

/**
 * The style problems in `text`. kind: "name" (a label, a document's name: a
 * short phrase in sentence case) or "text" (sentences: instructions, an FAQ
 * answer).
 */
export function styleIssues(text, { kind = "name" } = {}) {
  const value = String(text ?? "");
  const plain = value.trim();
  if (!plain) return [];
  const out = [];
  if (kind === "name" && !ARABIC.test(plain)) {
    const fixed = sentenceCase(plain);
    // Only a name with two or more capitalised words reads as Title Case.
    const capitals = (plain.match(/(^|\s)\p{Lu}\p{Ll}+/gu) || []).length;
    if (fixed && capitals >= 2) {
      out.push({ key: "title-case", short: "Capital on every word", text: `Use sentence case: “${fixed}”.`, fix: fixed });
    }
  }
  const spelling = ukSpelling(plain);
  if (spelling) out.push({ key: "us-spelling", short: "US spelling", text: `Use UK spelling: “${spelling.uk}”, not “${spelling.us}”.`, fix: spelling.fixed });
  const curly = curlyApostrophes(plain);
  if (curly !== plain) out.push({ key: "apostrophe", short: "Straight apostrophe", text: `Use a curly apostrophe: “${around(curly, firstDifference(plain, curly))}”.`, fix: curly });
  const doubled = plain.match(/\b(\p{L}+)\s+\1\b/iu);
  if (doubled) out.push({ key: "doubled-word", short: "Word written twice", text: `“${doubled[1]}” is written twice in a row.`, fix: plain.replace(/\b(\p{L}+)\s+\1\b/giu, "$1") });
  if (/ {2,}| [,.;:!?]/.test(plain)) {
    out.push({ key: "spacing", short: "Extra space", text: "It has a double space, or a space before a full stop or comma.", fix: plain.replace(/ {2,}/g, " ").replace(/ ([,.;:!?])/g, "$1") });
  }
  // "How long does it takes", "Does he needs": the verb after do / does stays plain.
  const agreement = plain.match(/\b(does|do|did|doesn’t|don’t|didn’t|can|will|must|should)\s+(it|he|she|they|you|we|i)\s+(\p{L}{3,}?)(s)\b/iu);
  if (agreement && !/(ss|us|is|as|ys)$/i.test(`${agreement[3]}${agreement[4]}`)) {
    const right = `${agreement[1]} ${agreement[2]} ${agreement[3]}`;
    out.push({ key: "grammar", short: "Grammar slip", text: `Write “${right}”, not “${agreement[0]}”.`, fix: plain.replace(agreement[0], right) });
  }
  if (kind === "text" && /^\p{Ll}/u.test(plain)) {
    out.push({ key: "lowercase", short: "Starts with a small letter", text: "Start it with a capital letter.", fix: plain.charAt(0).toUpperCase() + plain.slice(1) });
  }
  return out;
}

/**
 * Arabic in a document's name or instructions: one house format, the
 * English first and the Arabic in brackets ("Family civil extract (إخراج
 * قيد عائلي)"). Flags the other ways it has been written ("Photo | صورة").
 */
export function arabicStyle(text) {
  const value = String(text ?? "");
  if (!ARABIC.test(value)) return null;
  if (/[|/]\s*[؀-ۿ]|[؀-ۿ][^()]*[|/]/.test(value) || !/\([^)]*[؀-ۿ][^)]*\)/.test(value)) {
    return {
      key: "arabic-style",
      short: "Arabic written another way",
      text: "Write the Arabic in brackets after the English, for example “Family civil extract (إخراج قيد عائلي)”, so every document reads the same way.",
    };
  }
  return null;
}
